import { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { TALENT, SELECT_STYLE, TALENT_ARR } from '../../data/define';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ToolTip from '../common/ToolTip';
// import ReportChrtIntelligencSelectDialog from './ReportChartIntelligenceSelectDialog2';
import EmptyCase from '../common/EmptyCase';
import { useTranslation } from '../../hook/useTranslation';

const initDateLabel = ['', '', '', '', '', ''];

function ChangeFlowCumulativeChart({ changeToggle, compareResult }) {
  const { t } = useTranslation();
  // const [intelligenceSelectOpen, setIntelligenceSelectOpen] = useState(false);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [intelligence, setIntelligence] = useState(TALENT.RELATIONSHIP.id);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [dateLabel, setDateLabel] = useState(initDateLabel);
  const [cumulativeChartData, setCumulativeChartData] = useState([
    {
      name: '',
      data: [0, 0, 0, 0, 0, 0],
    },
  ]);

  useEffect(() => {
    if (cumulativeChartData[0]?.data?.length <= 3) {
      setEmptyOpen(true);
    } else {
      setEmptyOpen(false);
    }
  }, [cumulativeChartData]);

  useEffect(() => {
    if (!compareResult) return;
    const targetIntelli = compareResult?.kidAverage?.find((i) => i.intelligence === intelligence);
    const labels = targetIntelli?.result?.map((r) => moment(r.updatedAt).format('MM.DD'));
    const values = targetIntelli?.result?.map((r) => r.score);
    setDateLabel(['', ...labels, '']);
    setCumulativeChartData([
      {
        name: '',
        data: [null, ...values, null],
      },
    ]);
  }, [changeToggle, compareResult, intelligence]);

  // select 초기값 세팅
  useEffect(() => {
    if (!compareResult) return;
    const targetIntelli = compareResult?.kidAverage?.find((i) => i?.result?.length >= 2);
    setIntelligence(targetIntelli?.intelligence || TALENT.RELATIONSHIP.id);
  }, [compareResult]);

  const cumulativeChartOptions = {
    chart: {
      height: 300,
      type: 'area',
      width: '100%',
      toolbar: {
        show: false,
        tools: {
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      zoom: {
        enabled: false,
        type: 'x',
        autoScaleYaxis: false,
        zoomedArea: {
          fill: { color: '#fff', opacity: 0 },
          stroke: { color: '#fff', opacity: 0, width: 0 },
        },
      },
      selection: {
        enabled: false,
      },
      pan: {
        enabled: false,
        type: 'x',
      },
    },
    stroke: {
      curve: 'straight',
      width: 5,
    },
    fill: {
      type: 'solid',
      opacity: 0,
    },
    colors: ['#74DFE0'],
    dataLabels: {
      enabled: false,
    },
    grid: {
      show: true,
    },
    xaxis: {
      type: 'category',
      categories: dateLabel,
      labels: {
        show: true,
        rotate: 0,
        style: {
          colors: '#969696',
          fontSize: '12px',
          fontWeight: 400,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    markers: {
      size: 0,
      discrete: [
        {
          seriesIndex: 0,
          dataPointIndex: 1,
          fillColor: '#74DFE0',
          strokeColor: '#74DFE0',
          size: 6,
          shape: 'circle',
        },
        {
          seriesIndex: 0,
          dataPointIndex: dateLabel.length - 2,
          fillColor: '#74DFE0',
          strokeColor: '#74DFE0',
          size: 6,
          shape: 'circle',
        },
      ],
    },
    tooltip: {
      enabled: true,
      shared: true,
      followCursor: false,
      intersect: false,
      inverseOrder: false,
      custom({ series, seriesIndex, dataPointIndex }) {
        const value = series[seriesIndex][dataPointIndex];
        return `<div style="
          background: rgba(0,0,0,0.8);
          color: #fff;
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 12px;
        ">${value !== null ? value : '•'}점</div>`;
      },
      offsetY: -10,
      style: { fontSize: '12px', fontFamily: undefined },
      x: { show: false },
      markers: { show: true },
      items: { display: 'flex' },
      fixed: { enabled: false },
    },
  };

  return (
    <div className="w-full relative pt-[20px] pb-[20px]">
      <div className="flex items-center justify-between gap-2 mb-[20px]">
        <div className="flex items-center gap-2">
          <div className="text-[#1C1C1C] font-[700] text-[20px] text-left]">{t('report.change_flow.title')}</div>
          <ToolTip
            open={tooltipOpen}
            setOpen={setTooltipOpen}
            text={t('report.change_flow.tooltip')}
          />
        </div>
        <Select
          variant="outlined"
          value={intelligence || ''}
          onChange={(e) => setIntelligence(e.target.value)}
          IconComponent={KeyboardArrowDownIcon}
          size="small"
          sx={{
            ...SELECT_STYLE,
            height: '38px',
            borderRadius: '5px',
            border: '1px solid #E0E0E0',
            minWidth: '110px',
            fontWeight: 400,
          }}
        >
          {compareResult?.kidAverage?.length >= 1 &&
            compareResult?.kidAverage?.map((intelli) => (
              <MenuItem
                key={intelli.intelligence}
                value={intelli.intelligence}
                sx={{ fontSize: '14px' }}
                disabled={intelli.percentile === 0}
              >
                <span className="text-[14px] text-[#363636]">
                  {t(`talent.${TALENT_ARR.find((item) => item.id === intelli.intelligence)?.key}`)}
                </span>
              </MenuItem>
            ))}
        </Select>
        {/* <button
          type="button"
          className="flex justify-between items-center gap-1 text-[#363636]  text-[14px] font-[400] py-2 pl-4 pr-2 rounded-[6px] border border-[#D9D9D9]"
          onClick={() => setIntelligenceSelectOpen(true)}
        >
          <span>{intelligence.label}</span>
          <img
            src={IcChevronDown}
            alt="IcChevronDown"
            className="rotate-90"
            style={{
              transform: `rotate(${intelligenceSelectOpen ? 180 : 0}deg)`,
            }}
          />
        </button>
        <ReportChrtIntelligencSelectDialog
          open={intelligenceSelectOpen}
          onClose={() => setIntelligenceSelectOpen(false)}
          intelligence={intelligence}
          setIntelligence={setIntelligence}
          compareResult={compareResult}
        /> */}
      </div>
      <div className="relative w-full flex flex-col items-center justify-between">
        <div
          className="relative w-full"
          style={{
            width: '100vw',
            paddingLeft: '10px',
            paddingRight: '35px',
          }}
        >
          <ReactApexChart
            series={cumulativeChartData}
            options={cumulativeChartOptions as any}
            type="area"
            width="100%"
            height={300}
          />
          <div className="flex flex-col absolute top-5 right-[30px] gap-[25px] text-[#C6C6C6] text-[14px] font-[460]">
            <span>100</span>
            <span>80</span>
            <span>60</span>
            <span>40</span>
            <span>20</span>
            <span>0</span>
          </div>
        </div>
        <EmptyCase
          open={emptyOpen}
          text={t('report.change_flow.empty')}
        />
      </div>
    </div>
  );
}

export default ChangeFlowCumulativeChart;
